import axiosInstance from "./axiosInstance";

export type RoleEnum =
    | "ANALYST"
    | "CATEGORY_ADMIN"
    | "MARKETING"
    | "ORG_ROOT"
    | "REGISTERED_USER"
    | "ROOT"
    | "STORE_INVENTORY_MANAGER"
    | "SYS_ADMIN";

export const ALL_ROLES: RoleEnum[] = [
    "ANALYST",
    "CATEGORY_ADMIN",
    "MARKETING",
    "ORG_ROOT",
    "REGISTERED_USER",
    "ROOT",
    "STORE_INVENTORY_MANAGER",
    "SYS_ADMIN",
];

export interface UserSummary {
    id: number;
    email: string;
    roles?: string[];
}

export interface CreateUserRequest {
    username: string;
    email: string;
    password: string;
    roles: RoleEnum[];
}

function authHeader(token: string) {
    return { Authorization: `Bearer ${token}` };
}

/** Returns all SYS_ADMIN and CATEGORY_ADMIN users (the assignable admins). */
export async function listAssignableUsers(token: string): Promise<UserSummary[]> {
    const resp = await axiosInstance.get<UserSummary[]>("/users", {
        headers: authHeader(token),
    });
    return resp.data ?? [];
}

export async function getUserByEmail(token: string, email: string): Promise<UserSummary> {
    const resp = await axiosInstance.get<UserSummary>(`/users/email/${encodeURIComponent(email)}`, {
        headers: authHeader(token),
    });
    return resp.data;
}

export async function createUser(token: string, req: CreateUserRequest): Promise<UserSummary> {
    const resp = await axiosInstance.post<UserSummary>("/users", req, {
        headers: authHeader(token),
    });
    return resp.data;
}

export async function assignRoles(token: string, userId: number, roles: RoleEnum[]): Promise<void> {
    await axiosInstance.patch(`/users/${userId}/roles`, { roles }, {
        headers: authHeader(token),
    });
}

/** Admin sets a user's password directly (no current password required). */
export async function setUserPassword(token: string, userId: number, newPassword: string): Promise<void> {
    await axiosInstance.patch(`/users/${userId}/password`, { newPassword }, {
        headers: authHeader(token),
    });
}

export async function deleteUser(token: string, userId: number): Promise<void> {
    await axiosInstance.delete(`/users/${userId}`, {
        headers: authHeader(token),
    });
}

export async function getAssignableUsers(token: string): Promise<UserSummary[]> {
    const resp = await axiosInstance.get<UserSummary[]>("/users", {
        headers: authHeader(token),
    });
    return resp.data;
}

export async function changePassword(token: string, currentPassword: string, newPassword: string): Promise<void> {
    await axiosInstance.patch("/users/me/password", { currentPassword, newPassword }, {
        headers: authHeader(token),
    });
}
